import styles from "./A11y.module.css";
import { Button } from "@geti/identidade-visual-react";
import { useEffect, useRef, useState } from "react";
import { BsUniversalAccessCircle } from "react-icons/bs";
import { PiCursor } from "react-icons/pi";
import { SiExoscale } from "react-icons/si";
import { BiFontSize } from "react-icons/bi";
import { MdTextIncrease, MdTextDecrease, MdInvertColors } from "react-icons/md";
import { useDispatch } from "react-redux";
import { toggleColorInversion } from "../../Services/Slices/a11ySlice";

interface iA11y {
  setFontSize: React.Dispatch<React.SetStateAction<number>>;
  setGrayscale: React.Dispatch<React.SetStateAction<boolean>>;
  setCustomCursor: React.Dispatch<React.SetStateAction<boolean>>;
  isOpenModal: boolean;
  setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const A11y: React.FC<iA11y> = ({
  setFontSize,
  setGrayscale,
  setCustomCursor,
  isOpenModal,
  setIsOpenModal,
}) => {
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isResponsive, setIsResponsive] = useState(false);

  const stopPanelClose = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const handleColorInversion = () => {
    dispatch(toggleColorInversion());
  };

  const handleFontChange = (action: string) => {
    switch (action) {
      case "increase":
        setFontSize((prev) => Math.min(prev + 0.25, 2));
        break;
      case "decrease":
        setFontSize((prev) => Math.max(prev - 0.25, 0.75));
        break;
      case "reset":
        setFontSize(1);
        break;
    }
  };

  const handleToggleGrayscale = () => {
    setGrayscale((prev) => !prev);
  };

  const handleCursorSize = () => {
    setCustomCursor((prev) => !prev);
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        !isOpenModal &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpenModal(true);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpenModal, setIsOpenModal]);

  useEffect(() => {
    const handleResize = () => {
      setIsResponsive(window.innerWidth >= 769);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const iconSize = isResponsive ? 24 : 18;
  const buttonSize = isResponsive ? "icon-lg" : "icon-sm";

  return (
    <div ref={containerRef} className={styles.container} onClick={stopPanelClose}>
      {isOpenModal ? (
        <Button
          type="button"
          variant="default"
          size={buttonSize}
          onClick={() => setIsOpenModal(false)}
          className={styles.a11y}
          aria-label="Abrir opções de acessibilidade"
          aria-expanded={false}
        >
          <BsUniversalAccessCircle size={iconSize} />
        </Button>
      ) : (
        <div className={styles.controls} role="toolbar" aria-label="Opções de acessibilidade">
          <Button
            type="button"
            variant="default"
            size={buttonSize}
            className={styles.option}
            onClick={handleColorInversion}
            aria-label="Inverter cores"
          >
            <MdInvertColors size={iconSize} />
          </Button>
          <Button
            type="button"
            variant="default"
            size={buttonSize}
            className={styles.option}
            onClick={() => handleFontChange("increase")}
            aria-label="Aumentar fonte"
          >
            <MdTextIncrease size={iconSize} />
          </Button>
          <Button
            type="button"
            variant="default"
            size={buttonSize}
            className={styles.option}
            onClick={() => handleFontChange("reset")}
            aria-label="Restaurar tamanho da fonte"
          >
            <BiFontSize size={iconSize} />
          </Button>
          <Button
            type="button"
            variant="default"
            size={buttonSize}
            className={styles.option}
            onClick={() => handleFontChange("decrease")}
            aria-label="Diminuir fonte"
          >
            <MdTextDecrease size={iconSize} />
          </Button>
          <Button
            type="button"
            variant="default"
            size={buttonSize}
            className={styles.option}
            onClick={handleToggleGrayscale}
            aria-label="Alternar escala de cinza"
          >
            <SiExoscale size={iconSize} />
          </Button>
          {isResponsive && (
            <Button
              type="button"
              variant="default"
              size="icon-lg"
              className={styles.option}
              onClick={handleCursorSize}
              aria-label="Alternar cursor ampliado"
            >
              <PiCursor size={iconSize} />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default A11y;
